/**
 * lt-portfolio.js — slide-in reveal for the portfolio gallery.
 *
 * THE ONE RULE THIS FILE EXISTS TO ENFORCE
 * The photos are visible by default in CSS. This script *opts in* to the
 * animation by adding `.is-armed` to the section, and only after confirming
 * IntersectionObserver is available. So every failure mode — script blocked,
 * JS error, old browser, headless renderer, hidden tab — leaves the gallery
 * fully visible rather than blank. A different section on this theme once
 * shipped cards stranded at opacity 0 because it did the reverse.
 *
 * It also unobserves each item once revealed, so a 75-photo page doesn't keep
 * a large observer set alive while scrolling.
 */
(function () {
  'use strict';

  function init() {
    var sections = document.querySelectorAll('[data-lt-portfolio]');
    if (!sections.length) return;

    // No IntersectionObserver: leave the CSS default (everything visible).
    if (!('IntersectionObserver' in window)) return;

    // Respect the OS setting without relying on the CSS media query alone —
    // if motion is reduced there is no reason to arm observers at all.
    var reduced = window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced) return;

    Array.prototype.forEach.call(sections, function (section) {
      var items = section.querySelectorAll('.lt-portfolio__item');
      if (!items.length) return;

      // Tear down a previous run before starting another. shopify:section:load
      // fires on every theme-editor save; without this, five saves left five
      // live observers and five pending timers with no way to reclaim them
      // (measured in review). Storefront visitors never hit this path, but the
      // editor is where a merchant spends real time.
      if (section.__ltPortfolioTeardown) section.__ltPortfolioTeardown();

      section.classList.add('is-armed');

      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-in');
          io.unobserve(entry.target);
        });
      }, {
        // Start the reveal slightly before the photo reaches the viewport so
        // it is finished by the time it is properly in view.
        rootMargin: '0px 0px -8% 0px',
        threshold: 0.05
      });

      Array.prototype.forEach.call(items, function (el) { io.observe(el); });

      var netTimer = null;
      section.__ltPortfolioTeardown = function () {
        io.disconnect();
        if (netTimer) window.clearTimeout(netTimer);
        section.__ltPortfolioTeardown = null;
      };

      // Safety net — deliberately NOT a flat timer over everything.
      // The first version revealed every item after 4s regardless of whether
      // it had been scrolled to. An independent review measured the result:
      // at t=6s with no scrolling at all, the LAST photo on a 21,000px page
      // was already revealed. Only 3 photos are above the fold, so any
      // visitor who read the header for four seconds saw no animation on 71
      // of 74 photos — the net was quietly destroying the feature it existed
      // to protect.
      //
      // Now it only rescues items the observer genuinely never reported on,
      // and only while the page has not been scrolled — i.e. the real failure
      // case (observer silently broken) rather than the normal case (visitor
      // simply hasn't reached them yet).
      netTimer = window.setTimeout(function () {
        if ((window.scrollY || window.pageYOffset) > 40) return;  // user is scrolling: observer works
        var anyRevealed = false;
        Array.prototype.forEach.call(items, function (el) {
          if (el.classList.contains('is-in')) anyRevealed = true;
        });
        // If not a single item ever revealed, the observer is not working.
        // Show everything. Otherwise leave the rest to normal scrolling.
        if (!anyRevealed) {
          Array.prototype.forEach.call(items, function (el) {
            el.classList.add('is-in');
          });
        }
      }, 4000);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', init);
})();
